import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useAppContext, ACTION_TYPES } from '../Context/AppContext';
import { confirmPasswordReset } from '../Shared/publicApi';
import AuthPageLayout, { AuthHeaderLink } from '../Components/AuthPageLayout';

const ResetPassword = () => {
	const { dispatch } = useAppContext();
	const [searchParams] = useSearchParams();
	const uid = searchParams.get('uid') || '';
	const token = searchParams.get('token') || '';

	const [formData, setFormData] = useState({
		newPassword: '',
		confirmPassword: '',
	});
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);

	const missingParams = !uid || !token;

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Reset password' },
		});
		return () => {
			dispatch({
				type: ACTION_TYPES.SET_PAGE_TITLE,
				payload: { pageTitle: '' },
			});
		};
	}, [dispatch]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((current) => ({ ...current, [name]: value }));
		setError('');
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');

		if (formData.newPassword !== formData.confirmPassword) {
			setError('Passwords do not match.');
			return;
		}

		setLoading(true);
		try {
			await confirmPasswordReset({
				uid,
				token,
				new_password: formData.newPassword,
			});
			setSuccess(true);
		} catch (err) {
			const data = err?.response?.data;
			const message =
				data?.token?.[0] ||
				data?.new_password?.[0] ||
				data?.detail ||
				err?.message ||
				'Could not reset your password. Please try again.';
			setError(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<AuthPageLayout
			headerRight={<AuthHeaderLink to="/login">Sign in</AuthHeaderLink>}
		>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
					Reset your password
				</Typography>
				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ lineHeight: 1.75 }}
				>
					Choose a new password for your account.
				</Typography>
			</Box>

			{missingParams ? (
				<Alert severity="error" sx={{ mb: 2 }}>
					This reset link is invalid.{' '}
					<Typography
						component={Link}
						to="/forgot-password"
						variant="body2"
						sx={{ color: 'inherit', fontWeight: 600 }}
					>
						Request a new reset link
					</Typography>
				</Alert>
			) : success ? (
				<>
					<Alert severity="success" sx={{ mb: 3 }}>
						Your password has been updated. Sign in with your new password.
					</Alert>
					<Button
						component={Link}
						to="/login"
						variant="contained"
						color="warning"
						size="large"
						fullWidth
						sx={{ py: 1.5 }}
					>
						Sign in
					</Button>
				</>
			) : (
				<>
					{error && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{error}
						</Alert>
					)}

					<Paper
						elevation={0}
						sx={{
							p: { xs: 2.5, sm: 3 },
							borderRadius: 3,
							border: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Box component="form" onSubmit={handleSubmit}>
							<Stack spacing={2}>
								<TextField
									label="New password"
									name="newPassword"
									type="password"
									value={formData.newPassword}
									onChange={handleChange}
									required
									fullWidth
									autoComplete="new-password"
									autoFocus
								/>
								<TextField
									label="Confirm password"
									name="confirmPassword"
									type="password"
									value={formData.confirmPassword}
									onChange={handleChange}
									required
									fullWidth
									autoComplete="new-password"
								/>

								<Button
									type="submit"
									variant="contained"
									color="warning"
									size="large"
									disabled={loading}
									fullWidth
									sx={{ py: 1.5 }}
								>
									{loading ? 'Updating...' : 'Update password'}
								</Button>
							</Stack>
						</Box>
					</Paper>
				</>
			)}
		</AuthPageLayout>
	);
};

export default ResetPassword;
